/**
 * Document Type Utilities
 *
 * Helper functions for checking document source types across the application.
 * Provides a single source of truth for document type logic.
 */

/**
 * Source types for user-uploaded documents (PDF/DOCX/PPTX/XLSX/TXT).
 * The orchestrator ingest path stamps "document" (older rows "file"); the
 * legacy upload path used "custom"/"manual". All open via the document viewer.
 */
export const UPLOADED_DOCUMENT_SOURCE_TYPES = ['custom', 'manual', 'document', 'file'] as const;

/**
 * Check if a document is a user-uploaded document.
 * These documents can be viewed directly via the document viewer.
 *
 * @param sourceType - The source_type field from the document/source
 * @returns true if the document is an uploaded-document type
 */
export const isUserUploadedDocument = (sourceType: string): boolean => {
  return (UPLOADED_DOCUMENT_SOURCE_TYPES as readonly string[]).includes(sourceType);
};

/**
 * Check if a document is viewable in the UI.
 * Includes uploaded documents and FAQ documents that have view actions.
 *
 * @param sourceType - The source_type field from the document/source
 * @returns true if the document can be viewed in the application
 */
export const isViewableDocument = (sourceType: string): boolean => {
  return isUserUploadedDocument(sourceType) || sourceType === 'faq';
};

/**
 * Check if a document is an FAQ document.
 * FAQs open in a dialog rather than a separate viewer.
 *
 * @param sourceType - The source_type field from the document/source
 * @returns true if the document is an FAQ
 */
export const isFaqDocument = (sourceType: string): boolean => {
  return sourceType === 'faq';
};

/**
 * Check if a document is an external SAP document.
 * These documents open in a new tab to their external URL.
 *
 * @param sourceType - The source_type field from the document/source
 * @returns true if the document is an SAP external document
 */
export const isSapDocument = (sourceType: string): boolean => {
  return sourceType === 'sap';
};
